import { useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { Loader2, Plus, Users } from "lucide-react";

import { SendSMSModal } from "components/SendSMSModal/SendSMSModal";
import { Button } from "components/ui/Button";
import { MonthSelector } from "components/ui/MonthSelector/MonthSelector";
import { TabSwitcher } from "components/ui/TabSwitcher/TabSwitcher";
import { useDoctors } from "features/practice/hooks/useDoctors";

import styles from "./AppointmentsList.module.css";
import { AppointmentsBoardView } from "./components/AppointmentsBoardView/AppointmentsBoardView";
import { DoctorSearchInput } from "./components/DoctorSearchInput";
import { Filters } from "./components/Filters/Filters";
import { HorizontalCalendar } from "./components/HorizontalCalendar/HorizontalCalendar";
import { MonthView } from "./components/MonthView/MonthView";
import { useAppointments } from "./hooks/useAppointments";
import { resolveDoctorScope } from "./utils/resolve-doctor-scope";

const VIEW_MODE_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "month", label: "Month" },
];

const STORAGE_KEY_5MIN_APPOINTMENTS = "kinesin-5min-appointments";
const STORAGE_KEY_HIDE_DAYS_OFF = "kinesin-hide-days-off";

const getInitial5MinAppointment = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_5MIN_APPOINTMENTS);

    return stored === "true";
  } catch {
    return false;
  }
};

const getInitialHideDaysOff = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_HIDE_DAYS_OFF) === "true";
  } catch {
    return false;
  }
};

const isDoctorInRoster = (doctor) => {
  const rosterValue =
    doctor?.roster ??
    doctor?.permissions?.roster ??
    doctor?.staffMember?.roster;

  if (rosterValue === undefined || rosterValue === null) return true;

  return Boolean(rosterValue);
};

export const AppointmentsList = ({
  doctorId,
  myDoctorId,
  isSmall = false,
  noFilters = false,
  filterOutOfRosterDoctors = false,
  className,
  onAppointmentClick,
  onDateChange,
  noMaxHeight = false,
  maxHeight,
  isMonthly = false,
  multiDay = false,
  allowMonthView = false,
  showAddAppointmentModal: showAddAppointmentModalProp,
  setShowAddAppointmentModal: setShowAddAppointmentModalProp,
  title,
  titleIcon,
  hideTimeIndicator,
  extraControls,
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showMyOnly, setShowMyOnly] = useState(true);
  // Defaults to "day" (today) — isMonthly lets a caller force month mode from
  // the start. The Day/Month toggle itself only appears when allowMonthView
  // is set, and even then only when !noFilters.
  const [viewMode, setViewMode] = useState(isMonthly ? "month" : "day");
  const isMonthView = allowMonthView && viewMode === "month";
  // MonthView only ever renders one doctor's calendar. Callers that already
  // scope this list to a single doctor (doctorId/myDoctorId, e.g. "My
  // Appointments") don't need anything extra; a caller showing every doctor
  // (the main Appointments board) has to have the user pick exactly one
  // before month view can mean anything.
  const isSingleDoctorScoped = !!doctorId || !!myDoctorId;
  const isDoctorPickRequiredForMonth = isMonthView && !isSingleDoctorScoped;

  const handleDateChange = (date) => {
    setSelectedDate(date);
    onDateChange?.(date);
  };

  const handleSelectDateFromMonth = (date) => {
    setViewMode("day");
    handleDateChange(date);
  };
  // Two controls, two questions, so two states. They shared one before, and the
  // two disagreed about how many doctors it could hold: Filters is a
  // multi-select, DoctorSearchInput reads only the first id. Ticking three
  // doctors left the search box styled as filtered and captioned with the first
  // of them, while the board showed all three — and picking anything in the box
  // silently discarded the other two.
  //
  // Day view asks "which doctors am I looking at", and may answer with several.
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  // Month view asks "whose month is this", and can only answer with one.
  const [monthDoctorId, setMonthDoctorId] = useState(null);

  // Nothing to reset across the toggle any more: each view keeps its own answer,
  // so switching to Month and back no longer throws away the board's filter.
  const handleViewModeChange = (nextViewMode) => {
    setViewMode(nextViewMode);
  };
  const [showAddAppointmentModalInternal, setShowAddAppointmentModalInternal] =
    useState(false);
  const showAddAppointmentModal =
    showAddAppointmentModalProp ?? showAddAppointmentModalInternal;
  const setShowAddAppointmentModal =
    setShowAddAppointmentModalProp ?? setShowAddAppointmentModalInternal;
  const [selectedAppointments, setSelectedAppointments] = useState({});
  const [showSMSModal, setShowSMSModal] = useState(false);

  // Use the roster-scoped endpoint (/staffmember/getbyroster/true) so the
  // filter + type-ahead only show staff who are actually on roster —
  // matches what the user sees as a "doctor" on the schedule.
  const { data: doctors } = useDoctors();

  const rosterFilteredDoctors = useMemo(
    () =>
      filterOutOfRosterDoctors
        ? (doctors ?? []).filter((doctor) => isDoctorInRoster(doctor))
        : doctors ?? [],
    [doctors, filterOutOfRosterDoctors]
  );

  const getMonthRange = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { firstDay, lastDay };
  };

  const { firstDay, lastDay } = getMonthRange(selectedDate);

  const [is5minsAppointment, setIs5minsAppointment] = useState(
    getInitial5MinAppointment
  );
  const [hideDaysOff, setHideDaysOff] = useState(getInitialHideDaysOff);
  const showHideDaysOffToggle = !noFilters;

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY_5MIN_APPOINTMENTS,
        String(is5minsAppointment)
      );
    } catch {
      // Ignore storage errors
    }
  }, [is5minsAppointment]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_HIDE_DAYS_OFF, String(hideDaysOff));
    } catch {
      // Ignore storage errors
    }
  }, [hideDaysOff]);

  const effectiveDoctorId =
    doctorId ?? (myDoctorId && showMyOnly ? myDoctorId : undefined);

  const hasMonthDoctor = !!monthDoctorId;

  // With one doctor on the roster there is nothing to filter between, so the
  // picker would only add a control that can't change what's on screen.
  const hasMultipleDoctors = rosterFilteredDoctors.length > 1;

  // Whichever of the two the current view is actually asking about, as ids the
  // query can send. A caller-scoped list (doctorId/myDoctorId) overrides both.
  const doctorScope = useMemo(
    () =>
      resolveDoctorScope({
        effectiveDoctorId,
        isMonthView,
        monthDoctorId,
        selectedDoctors,
      }),
    [effectiveDoctorId, isMonthView, monthDoctorId, selectedDoctors]
  );

  // In a doctor-scoped view (noFilters, e.g. "My Appointments") the doctor id
  // arrives asynchronously. Until it resolves, selectedDoctors is empty, which
  // would fetch EVERY doctor and briefly render the wrong columns before the
  // real one loads. Hold the query until the scope is known. An unscoped
  // month view holds it further still, until the user has picked the one
  // doctor it's going to show — otherwise it would fetch every doctor and
  // MonthView would silently render whichever one happened to come first.
  const isDoctorScopeReady =
    (!noFilters || !!effectiveDoctorId || doctorScope.length > 0) &&
    (!isDoctorPickRequiredForMonth || hasMonthDoctor);

  const { data: appointments, isPending } = useAppointments({
    is5minsAppointment,
    hideDaysOff: showHideDaysOffToggle && hideDaysOff,
    selectedStartDate: isMonthView ? firstDay : selectedDate,
    selectedEndDate: isMonthView
      ? lastDay
      : multiDay
      ? addDays(selectedDate, 6)
      : selectedDate,
    selectedDoctors: doctorScope,
    filterOutOfRosterDoctors,
    enabled: isDoctorScopeReady,
  });

  // Month view only ever shows one doctor's calendar — name them (and the
  // month) inline in the controls row rather than as a separate title row,
  // which would just push the calendar further down for no benefit.
  const monthViewDoctorId = isMonthView
    ? effectiveDoctorId ?? monthDoctorId ?? undefined
    : undefined;
  const monthViewDoctor = monthViewDoctorId
    ? rosterFilteredDoctors.find(
        (doctor) => String(doctor.id) === String(monthViewDoctorId)
      )
    : undefined;

  const selectedCount = Object.keys(selectedAppointments).length;
  const hasDoctors = appointments?.data.some(
    (day) => (day.doctors ?? []).length > 0
  );

  return (
    <>
      <div className={`${styles.appointmentsListWrapper} ${className}`}>
        {title && (
          <div className={styles.titleRow}>
            <h2 className={styles.titleText}>
              {titleIcon}
              {title}
            </h2>
            {!noFilters && !myDoctorId && (
              <Button
                variant="text"
                size="small"
                startIcon={Plus}
                onClick={() => setShowAddAppointmentModal(true)}
              >
                Add appointment
              </Button>
            )}
          </div>
        )}
        <div
          className={`${styles.appointmentsListHeader} ${
            isSmall ? styles.appointmentsListHeaderSmall : ""
          }`}
        >
          {/* Row 1: view tabs on the left, actions on the right.
              Row 2: the toggles on the left, staff search and month/filters
              on the right — the search sits beside the Day/Month toggle since
              that's what it's there to drive in month view. */}
          <div className={styles.controlsRow}>
            <div className={styles.controlsLeft}>{extraControls}</div>

            <div className={styles.controlsToggles}>
              {!isMonthView && (
                <label className={styles.durationToggleLabel}>
                  <div className={styles.durationToggleContainer}>
                    <input
                      type="checkbox"
                      className={styles.durationToggleInput}
                      checked={is5minsAppointment}
                      onChange={(e) => setIs5minsAppointment(e.target.checked)}
                      role="switch"
                    />
                    <span className={styles.durationToggleSlider}></span>
                  </div>
                  <span className={styles.durationToggleText}>
                    5 mins appointments
                  </span>
                </label>
              )}

              {/* Not relevant in month view — a day off already reads
                  clearly on the month grid itself, so show the doctor +
                  month title in this slot instead. */}
              {isMonthView ? (
                !!monthViewDoctor && (
                  <span className={styles.monthViewDoctorTitle}>
                    {monthViewDoctor.firstName} {monthViewDoctor.lastName}
                    <span className={styles.monthViewDoctorTitleMonth}>
                      {format(selectedDate, "MMMM yyyy")}
                    </span>
                  </span>
                )
              ) : (
                !!showHideDaysOffToggle && (
                  <label className={styles.durationToggleLabel}>
                    <div className={styles.durationToggleContainer}>
                      <input
                        type="checkbox"
                        className={styles.durationToggleInput}
                        checked={hideDaysOff}
                        onChange={(e) => setHideDaysOff(e.target.checked)}
                        role="switch"
                      />
                      <span className={styles.durationToggleSlider}></span>
                    </div>
                    <span className={styles.durationToggleText}>
                      Hide days off
                    </span>
                  </label>
                )
              )}
            </div>

            <div className={styles.controlsButtons}>
              {!noFilters && selectedCount > 0 && (
                <Button variant="outline" onClick={() => setShowSMSModal(true)}>
                  Send SMS ({selectedCount})
                </Button>
              )}
              {myDoctorId && (
                <TabSwitcher
                  size="small"
                  value={showMyOnly ? "for-me" : "all"}
                  onChange={(v) => setShowMyOnly(v === "for-me")}
                  options={[
                    { value: "for-me", label: "For me" },
                    { value: "all", label: "All" },
                  ]}
                />
              )}
              {(!noFilters && !title) || myDoctorId ? (
                <Button
                  variant="primary"
                  endIcon={Plus}
                  onClick={() => setShowAddAppointmentModal(true)}
                >
                  Add appointment
                </Button>
              ) : null}
            </div>

            <div className={styles.controlsPeriod}>
              {!noFilters && allowMonthView && (
                <TabSwitcher
                  size="small"
                  value={viewMode}
                  onChange={handleViewModeChange}
                  options={VIEW_MODE_OPTIONS}
                />
              )}
              {/* Month view only, because it is the only view that needs exactly
                  one doctor. In Day view it sat beside Filters answering the same
                  question with half the range, which is what made the pair read
                  as broken — Day view searches inside Filters instead. */}
              {!!isDoctorPickRequiredForMonth && (
                <div className={styles.controlsPeriodSearch}>
                  <DoctorSearchInput
                    doctors={rosterFilteredDoctors}
                    selectedDoctorId={monthDoctorId}
                    onSelectDoctor={setMonthDoctorId}
                  />
                </div>
              )}
              <MonthSelector
                key={`${selectedDate.getFullYear()}-${selectedDate.getMonth()}`}
                selectedDate={selectedDate}
                setSelectedDate={handleDateChange}
              />
              {!noFilters && !isMonthView && hasMultipleDoctors && (
                <Filters
                  selectedDoctors={selectedDoctors}
                  setSelectedDoctors={setSelectedDoctors}
                  doctors={rosterFilteredDoctors}
                />
              )}
            </div>
          </div>

          {/* Row 2: full-width calendar — redundant with the month grid itself
              once that's what's on screen, so it only shows in day view. */}
          {!isMonthView && (
            <div className={styles.calendarRow}>
              <HorizontalCalendar
                selectedDate={selectedDate}
                setSelectedDate={handleDateChange}
              />
            </div>
          )}
        </div>
        {isDoctorPickRequiredForMonth && !hasMonthDoctor ? (
          <div className={`${styles.patientDetailCard} bg`}>
            <div className={styles.emptyState}>
              <Users
                size={40}
                style={{ color: "var(--green-800)", marginBottom: 8 }}
              />
              <p className={styles.emptyStateTitle}>Select a staff member</p>
              <p className={styles.emptyStateText}>
                Month view shows one calendar at a time — search for a staff
                member above to see their month.
              </p>
            </div>
          </div>
        ) : (
          <>
            {!appointments && isPending && (
              <div className={`${styles.patientDetailCard} bg`}>
                <div className={styles.emptyState}>
                  <Loader2 className={styles.loadingSpinner} size={24} />
                  <p className={styles.emptyStateTitle}>Loading data...</p>
                  <p className={styles.emptyStateText}>
                    Please wait while we fetch the appointments.
                  </p>
                </div>
              </div>
            )}
            {!appointments && !isPending && (
              <div className={`${styles.patientDetailCard} bg`}>
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateTitle}>No appointments found</p>
                  <p className={styles.emptyStateText}>
                    Please check the filters and try again.
                  </p>
                </div>
              </div>
            )}
            {appointments && !hasDoctors && (
              <div className={styles.patientDetailCard}>
                <div className={styles.emptyState}>
                  <Users
                    size={40}
                    style={{ color: "var(--green-800)", marginBottom: 8 }}
                  />
                  <p className={styles.emptyStateTitle}>No doctors available</p>
                  <p className={styles.emptyStateText}>
                    There are no doctors scheduled for this date. Try adjusting
                    the filters or selecting a different date.
                  </p>
                </div>
              </div>
            )}
            {appointments && hasDoctors && (
              <div
                className={`${styles.appointmentsListTableWrapper} ${
                  noMaxHeight
                    ? styles.appointmentsListTableWrapperNoMaxHeight
                    : ""
                }`}
              >
                {isMonthView ? (
                  <MonthView
                    data={appointments.data}
                    selectedDate={selectedDate}
                    onSelectDate={handleSelectDateFromMonth}
                  />
                ) : (
                  <AppointmentsBoardView
                    hideTimeIndicator={hideTimeIndicator}
                    data={appointments.data}
                    timeSlots={appointments.timeSlots}
                    selectedDate={selectedDate}
                    isSmall={isSmall}
                    onAppointmentClick={onAppointmentClick}
                    showAddAppointmentModal={showAddAppointmentModal}
                    setShowAddAppointmentModal={setShowAddAppointmentModal}
                    noMaxHeight={noMaxHeight}
                    maxHeight={noMaxHeight ? undefined : maxHeight}
                    multiDay={multiDay}
                    selectedAppointments={selectedAppointments}
                    setSelectedAppointments={setSelectedAppointments}
                    slotDurationMinutes={is5minsAppointment ? 5 : 15}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showSMSModal && (
        <SendSMSModal
          onClose={() => {
            setShowSMSModal(false);
            setSelectedAppointments({});
          }}
          selectedRows={selectedAppointments}
          reportType="appointments"
        />
      )}
    </>
  );
};
